/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { CustomTooltip, dateUtils } from '@opengeoweb/shared';
import type { WMJSDimension } from '@opengeoweb/webmap';

type LayerInfoDimension = Pick<WMJSDimension, 'name' | 'currentValue'>;

const getShortenedDimensionName = (dimName: string): string => {
  switch (dimName) {
    case 'time':
      return 'val';
    case 'reference_time':
      return 'ref';
    default: {
      const nameParts = dimName.split('_');
      if (nameParts[0] === 'pressure') {
        return 'p';
      }
      if (nameParts[0] === 'height') {
        return 'h';
      }
      return dimName;
    }
  }
};

const formatDimensionVal = (dimName: string, dimVal: string): string => {
  switch (dimName) {
    case 'time':
    case 'reference_time': {
      const date = dateUtils.stringToDate(
        dimVal,
        "yyyy-MM-dd'T'HH:mm:ss'Z'",
        true,
      );
      if (date) {
        return dateUtils.dateToString(date, "MM-dd HH:mm'Z'", true)!;
      }
      return dimVal;
    }
    default: {
      return dimVal;
    }
  }
};

const getDimensionInfo = (dimension: LayerInfoDimension): string => {
  const dimensionName = getShortenedDimensionName(dimension.name);
  const formattedValue = formatDimensionVal(
    dimension.name,
    dimension.currentValue,
  );
  return `${dimensionName} ${formattedValue}`;
};

export interface ShowLayerInfoProps {
  title?: string;
  dimensions: LayerInfoDimension[];
  children?: React.ReactNode;
}

/** Debug overlay row with the layer title and its current dimension values. */
export const ShowLayerInfo: React.FC<ShowLayerInfoProps> = ({
  title,
  dimensions,
  children,
}: ShowLayerInfoProps) => {
  const tooltipText = (
    <span>
      {title}: <br />
      {dimensions.map((dim, index) => (
        <span key={`${dim.name}-${dim.currentValue}`}>
          {dim.name}: {formatDimensionVal(dim.name, dim.currentValue)}
          {index < dimensions.length - 1 && <br />}
        </span>
      ))}
    </span>
  );

  return (
    <CustomTooltip title={tooltipText}>
      <div
        data-testid="debug-info"
        style={{
          position: 'relative',
          width: 'calc(100% - 60px)',
          left: '50px',
          right: '10px',
          marginTop: '12px',
          marginBottom: '-10px',
        }}
      >
        {children}
        <div
          style={{
            left: '10px',
            paddingTop: '3px',
            fontSize: '12px',
            fontFamily: 'Roboto',
            opacity: 0.85,
            width: '100%',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              verticalAlign: 'top',
              width: '25%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '12px',
              paddingRight: '4px',
              paddingLeft: '4px',
              color: '#051039',
              backgroundColor: 'rgba(236, 237, 238, 0.7)',
              borderRadius: 2,
            }}
          >
            {title}
          </span>
          <span
            style={{ display: 'inline-block', width: '75%', opacity: '0.7' }}
          >
            {dimensions.map((dimension) => {
              return (
                <span
                  key={dimension.name}
                  style={{
                    margin: '0 0 0 0px',
                    padding: '1px 5px',
                    background: 'blue',
                    borderRadius: 2,
                    color: 'white',
                    fontSize: '11px',
                  }}
                  title={`${dimension.name} ${formatDimensionVal(
                    dimension.name,
                    dimension.currentValue,
                  )}`}
                >
                  {getDimensionInfo(dimension)}
                </span>
              );
            })}
          </span>
        </div>
      </div>
    </CustomTooltip>
  );
};
